import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api.js';
import { ActionInput, CardFrame, Chip, OptionButton, SectionHeading, useAccess } from '../parts/index.js';
import { useViewing, viewingRevision } from '../view-as.js';
import { accessApi } from '../admin/access/api.js';
import { ConfirmChange, useChangeFlow } from '../admin/access/change-flow.js';
import { describeChange, when } from '../admin/access/model.js';
import type { AccessPageData, Person } from '../admin/access/types.js';
import '../admin/access/access.css';

/** A People record id is not a login id. Only the explicit API link may open this panel. */
export function HowCobrainReads({ store, principalId, name }: { store: string; principalId?: string | null; name: string }) {
  const view = useViewing();
  const access = useAccess();
  // Wait for the access answer before asking: a panel that mounts first remounts when it lands and aborts its own read
  // (CI smoke, /people/p2, 15 Oct). Only an admin may read the admin access answer, so nobody else asks for it.
  if (!principalId || !access?.person.role.endsWith('admin')) return null;
  return <ReadPanel key={`${store}:${principalId}:${viewingRevision()}:${access?.person.principalId}:${!!view || !!access?.viewing}`}
    store={store} principalId={principalId} name={name} />;
}
function ReadPanel({ store, principalId, name }: { store: string; principalId: string; name: string }) {
  const [data, setData] = useState<AccessPageData | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const live = useRef(true);
  const epoch = useRef(viewingRevision());
  const active = () => live.current && epoch.current === viewingRevision();
  const load = async (current: () => boolean = () => true) => {
    try {
      // The admin access answer is the authority; no role inference on a record page.
      const data = await accessApi.read(store);
      if (active() && current()) { setData(data); setPerson(data.people.find(p => p.principalId === principalId) ?? null); setError(null); }
    } catch (e) {
      if (active() && current()) {
        setPerson(null);
        setError(e instanceof ApiError && [401, 403, 404].includes(e.status) ? null : 'Access could not be loaded.');
      }
      throw e;
    }
  };
  const flow = useChangeFlow(store, load, (change, result) => data ? describeChange(change, data, true, result.done ?? result.words) : result.done ?? result.words);
  useEffect(() => {
    // Never aborted: the record page remounts this panel as it refreshes, and an aborted read is a failed request to
    // the smoke suite (/people/p2, 15 Oct). A late answer is ignored instead.
    live.current = true; let current = true;
    void load(() => current).catch(() => {});
    return () => { live.current = false; current = false; };
  }, [store, principalId, retry]);
  if (!person) return error ? <div className="access-page"><p className="access-warning" role="alert">{error}</p><OptionButton opensOnly onClick={() => setRetry(n => n + 1)}>Try again</OptionButton>{flow.notice}</div> : null;
  return <div className="access-page access-stack"><CardFrame variant="panel">
    <SectionHeading title={`How Cobrain reads for ${name}`} />
    <dl className="access-facts">
      <div><dt>Triage load time</dt><dd>{person.lastTriageLoad.why}</dd></div>
      <div><dt>Tasks load time</dt><dd>{person.lastTasksLoad.why}</dd></div>
      <div><dt>Version</dt><dd>{person.version.label}</dd></div>
      <div><dt>History read in full</dt><dd>{person.historyMonths} months</dd></div>
      <div><dt>Company intelligence last written</dt><dd>{when(person.intelligenceWrittenAt)}</dd></div>
    </dl>
    <label className="access-row"><ActionInput type="checkbox" className={`access-tick${flow.pending ? ' access-unsaved' : ''}`} aria-disabled={flow.busy || !!flow.pending}
      checked={flow.pending && 'enabled' in flow.pending.change ? flow.pending.change.enabled : person.historyWhole}
      onChange={e => { if (!flow.busy && !flow.pending) void flow.begin('history', { kind: 'person_history_whole', principalId, enabled: e.target.checked }); }} />Read whole history{flow.pending ? <Chip className="access-unsaved-chip">Not saved yet</Chip> : null}</label>
    {flow.pending ? <ConfirmChange flow={flow} title={`${person.displayName}: ${'enabled' in flow.pending.change && flow.pending.change.enabled ? 'read whole history' : 'stop reading older history'}`} /> : null}
  </CardFrame>{flow.notice}</div>;
}
